import type {
  FichaDiariaItem,
  UndMedidaOption,
} from "../../../services/controleProducaoService";
import {
  computeSugestaoProducao,
  formatQuantidadeComUnd,
  undNomeById,
} from "../../../utils/controleProducaoQuantidade";

export type BlocoSetorFicha = {
  setorId: number | null;
  setorNome: string | null;
  itens: FichaDiariaItem[];
};

function chaveLinha(item: FichaDiariaItem): string {
  return `${item.itemId}-${item.lojaIdOutra ?? "local"}`;
}

function quantidadeNumerica(texto: string | null | undefined): string {
  const t = (texto ?? "").trim();
  if (!t) return "";
  const match = t.match(/^([\d.,]+)/);
  return match ? match[1] : t;
}

function baseValorComPercentual(
  item: FichaDiariaItem,
  percentual: number
): string {
  const raw = String(item.baseValor ?? "").trim();
  if (!raw || percentual >= 99.995) return raw;
  const val = parseFloat(raw.replace(",", "."));
  if (Number.isNaN(val)) return raw;
  return String(Math.round(((val * percentual) / 100) * 100) / 100);
}

function baseComPercentual(
  item: FichaDiariaItem,
  percentual: number,
  unds: UndMedidaOption[]
): string {
  if (percentual >= 99.995 || item.baseValor == null || item.baseValor === "") {
    return item.base;
  }
  const ajustado = baseValorComPercentual(item, percentual);
  if (!ajustado) return item.base;
  return formatQuantidadeComUnd(ajustado, item.undMedidaIdBase, unds);
}

function sobraComPercentual(
  sobra: string | null | undefined,
  percentual: number
): string {
  const txt = quantidadeNumerica(sobra);
  if (!txt || percentual >= 99.995) return txt;
  const val = parseFloat(txt.replace(",", "."));
  if (Number.isNaN(val)) return txt;
  return String(Math.round(((val * percentual) / 100) * 100) / 100);
}

function camposRateadosDoSetor(
  item: FichaDiariaItem,
  percentual: number,
  unds: UndMedidaOption[]
): Pick<
  FichaDiariaItem,
  | "base"
  | "baseValor"
  | "sobra"
  | "sobraExibicao"
  | "sugestaoProducao"
  | "sugestaoUndMedidaIdProducao"
> {
  const baseValor = baseValorComPercentual(item, percentual);
  const sobraPersistida = sobraComPercentual(item.sobra, percentual);
  const sobraRef = sobraComPercentual(
    quantidadeNumerica(item.sobraExibicao) || item.sobraReferencia,
    percentual
  );
  const sobraParaSugestao = sobraPersistida || sobraRef;
  const undSobraId =
    (sobraPersistida
      ? item.undMedidaIdSobra
      : (item.undMedidaIdSobraReferencia ?? item.undMedidaIdSobra)) ??
    item.undMedidaIdBase;
  return {
    base: baseComPercentual(item, percentual, unds),
    baseValor,
    sobra: sobraPersistida,
    sobraExibicao: formatQuantidadeComUnd(sobraParaSugestao, undSobraId, unds),
    sugestaoProducao: computeSugestaoProducao(
      baseValor,
      undNomeById(item.undMedidaIdBase, unds) || null,
      sobraParaSugestao || null,
      undNomeById(undSobraId, unds) || null
    ),
    sugestaoUndMedidaIdProducao:
      item.undMedidaIdBase ?? item.sugestaoUndMedidaIdProducao,
  };
}

export function agruparItensFichaPorSetor(
  itens: FichaDiariaItem[],
  setoresOrdem: Array<{ setorId: number; setorNome: string }> | undefined,
  unds: UndMedidaOption[]
): BlocoSetorFicha[] {
  const fromItems = new Map<
    number,
    { setorId: number; setorNome: string; ordem: number }
  >();
  for (const item of itens) {
    for (const setor of item.setores ?? []) {
      if (!fromItems.has(setor.id)) {
        fromItems.set(setor.id, {
          setorId: setor.id,
          setorNome: setor.nome,
          ordem: setor.ordem,
        });
      }
    }
  }

  const ordem = setoresOrdem?.length
    ? setoresOrdem.map((setor) => ({
        setorId: setor.setorId,
        setorNome: setor.setorNome,
        ordem: fromItems.get(setor.setorId)?.ordem ?? 999,
      }))
    : Array.from(fromItems.values()).sort(
        (a, b) => a.ordem - b.ordem || a.setorNome.localeCompare(b.setorNome)
      );

  const usados = new Set<string>();
  const blocos: BlocoSetorFicha[] = [];

  for (const setor of ordem) {
    const doSetor: FichaDiariaItem[] = [];
    for (const item of itens) {
      const vinculo = (item.setores ?? []).find((s) => s.id === setor.setorId);
      if (!vinculo) continue;
      usados.add(chaveLinha(item));
      const percentual = vinculo.percentual ?? 100;
      // O vínculo traz a produção do setor na loja local. A linha da outra
      // unidade já carrega a produção dela; sobrepor repetia o número local.
      const overlayProducao =
        item.lojaIdOutra == null &&
        (vinculo.producao !== undefined || vinculo.producaoChecada !== undefined);
      doSetor.push({
        ...item,
        ...camposRateadosDoSetor(item, percentual, unds),
        ...(overlayProducao
          ? {
              producao: vinculo.producao ?? "",
              undMedidaIdProducao: vinculo.undMedidaIdProducao ?? undefined,
              producaoChecada: vinculo.producaoChecada ?? false,
              naoProduzidoPelaUnidade:
                vinculo.naoProduzidoPelaUnidade ?? false,
              justificativaNaoProduziu:
                vinculo.justificativaNaoProduziu ?? "",
              observacaoProducao: vinculo.observacaoProducao ?? "",
            }
          : {}),
        cronometragem:
          vinculo.cronometragem !== undefined
            ? vinculo.cronometragem
            : item.cronometragem,
      });
    }
    if (doSetor.length) {
      blocos.push({
        setorId: setor.setorId,
        setorNome: setor.setorNome,
        itens: doSetor,
      });
    }
  }

  const resto = itens.filter((item) => !usados.has(chaveLinha(item)));
  if (resto.length) {
    blocos.push({
      setorId: null,
      setorNome: blocos.length ? "Sem setor" : null,
      itens: resto.map((item) => ({
        ...item,
        ...camposRateadosDoSetor(item, 100, unds),
      })),
    });
  }

  return blocos;
}
